/**
 * Link Encompass Template Dialog
 *
 * Allows users to link an Encompass Investor Template to an investor.
 * Fetches available templates from Encompass and lets user select one.
 */
/* eslint-disable react-refresh/only-export-components */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Link2, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { investorEncompassTemplateService } from '@/services/investorEncompassTemplateService';
import type { InvestorEncompassTemplate } from '@/types/investor-encompass';
import { PRODUCT_TYPES, type ProductType } from '@/types/investor';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('LinkEncompassTemplateDialog', 'ui');

const ProductScopeGrid: React.FC<{
  selected: ProductType[];
  onChange: (products: ProductType[]) => void;
}> = ({ selected, onChange }) => {
  const selectedSet = new Set(selected);
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      {PRODUCT_TYPES.map((product) => (
        <label key={product} className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5 text-xs">
          <Checkbox
            checked={selectedSet.has(product)}
            onCheckedChange={(value) => {
              const next = new Set(selectedSet);
              if (value === true) {
                next.add(product);
              } else {
                next.delete(product);
              }
              onChange(Array.from(next));
            }}
          />
          <span>{product}</span>
        </label>
      ))}
    </div>
  );
};

interface AvailableTemplate {
  id: string;
  templateName: string;
  path?: string;
  investorName?: string;
  investorCode?: string;
}

interface LinkEncompassTemplateDialogProps {
  servicerSelectionMode?: 'product_scope' | 'per_loan';
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investorId: string;
  onLinkSuccess: (template: InvestorEncompassTemplate) => void;
}

export function buildTemplateProductScopesForLink(
  existingTemplates: InvestorEncompassTemplate[],
  selectedEncompassTemplateIds: string[],
  productScopes: Record<string, ProductType[]>,
  mode: 'product_scope' | 'per_loan' = 'product_scope'
): Array<{ encompassTemplateId: string; products: ProductType[] }> {
  const existingScopes = existingTemplates.map((template) => ({
    encompassTemplateId: template.encompassTemplateId,
    products: productScopes[template.encompassTemplateId] ?? template.applicableProducts ?? []
  }));
  const existingIds = new Set(existingTemplates.map((template) => template.encompassTemplateId));
  const newScopes = selectedEncompassTemplateIds.filter((id) => !existingIds.has(id)).map((encompassTemplateId) => ({
    encompassTemplateId,
    products: productScopes[encompassTemplateId] ?? []
  }));
  return [...existingScopes, ...newScopes].map(scope => mode === 'per_loan' ? { ...scope, products: [] } : scope);
}

export function findDuplicateScopeProducts(
  scopes: Array<{ encompassTemplateId: string; products: ProductType[] }>
): ProductType[] {
  const seen = new Set<ProductType>();
  const duplicates = new Set<ProductType>();
  for (const scope of scopes) {
    for (const product of scope.products) {
      if (seen.has(product)) {
        duplicates.add(product);
      }
      seen.add(product);
    }
  }
  return [...duplicates];
}

function firstWord(name: string): string {
  return name.trim().split(/\s+/)[0]?.toLowerCase() ?? '';
}

export const LinkEncompassTemplateDialog: React.FC<LinkEncompassTemplateDialogProps> = ({
  open,
  onOpenChange,
  investorId,
  onLinkSuccess,
  servicerSelectionMode = 'product_scope'
}) => {
  const perLoan = servicerSelectionMode === 'per_loan';
  const [isLoading, setIsLoading] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<AvailableTemplate[]>([]);
  const [linkedTemplates, setLinkedTemplates] = useState<Map<string, { investorId: string; investorName: string }>>(new Map());
  const [existingInvestorTemplates, setExistingInvestorTemplates] = useState<InvestorEncompassTemplate[]>([]);
  const [selectedTemplateIds, setSelectedTemplateIds] = useState<string[]>([]);
  const [productScopes, setProductScopes] = useState<Record<string, ProductType[]>>({});

  const fetchAvailableTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Fetch available templates and linked templates in parallel
      const [templates, linked, currentTemplates] = await Promise.all([
        investorEncompassTemplateService.listAvailableTemplates(),
        investorEncompassTemplateService.getLinkedTemplates(),
        investorEncompassTemplateService.getTemplatesByInvestorId(investorId)
      ]);
      setAvailableTemplates(templates);
      setLinkedTemplates(linked);
      setExistingInvestorTemplates(currentTemplates);
      setProductScopes((prev) => {
        const next = { ...prev };
        for (const template of currentTemplates) {
          if (!next[template.encompassTemplateId]) {
            next[template.encompassTemplateId] = template.applicableProducts ?? [];
          }
        }
        return next;
      });
      log.info('Fetched available templates', {
        count: templates.length,
        linkedCount: linked.size,
        currentCount: currentTemplates.length
      });
    } catch (err: unknown) {
      log.error('Failed to fetch available templates', {}, err instanceof Error ? err : undefined);
      setError(err instanceof Error ? err.message : 'Failed to fetch templates from Encompass');
    } finally {
      setIsLoading(false);
    }
  }, [investorId]);

  // Fetch available templates when dialog opens
  useEffect(() => {
    if (open) {
      void fetchAvailableTemplates();
    } else {
      // Reset state when dialog closes
      setExistingInvestorTemplates([]);
      setSelectedTemplateIds([]);
      setProductScopes({});
      setError(null);
    }
  }, [open, fetchAvailableTemplates]);

  const handleLink = async () => {
    if (selectedTemplateIds.length === 0) {
      toast.error('Please select at least one template');
      return;
    }

    const selectedTemplates = [
      ...existingInvestorTemplates.map((template) => ({
        id: template.encompassTemplateId,
        templateName: template.templateName
      })),
      ...availableTemplates.filter((template) => selectedTemplateIds.includes(template.id))
    ];
    const firstWords = new Set(
      selectedTemplates.map((template) => template.templateName.trim().split(/\s+/)[0]?.toLowerCase()).filter(Boolean)
    );
    if (firstWords.size > 1) {
      setError('Selected templates must share the same first word so unrelated investors are not grouped together.');
      return;
    }
    const templateScopes = buildTemplateProductScopesForLink(existingInvestorTemplates, selectedTemplateIds, productScopes, servicerSelectionMode);
    const missingScope = templateScopes.find((scope) => scope.products.length === 0);
    if (!perLoan && missingScope) {
      setError('Assign at least one product to each selected template.');
      return;
    }
    const duplicateProducts = findDuplicateScopeProducts(templateScopes);
    if (duplicateProducts.length > 0) {
      setError(`Each product can only be assigned once. Remove ${duplicateProducts.join(', ')} from the current template scope before linking.`);
      return;
    }

    setIsLinking(true);
    setError(null);

    try {
      await investorEncompassTemplateService.linkTemplatesWithProductScopes(investorId, templateScopes);

      // Fetch all templates and find the newly linked one
      const allTemplates = await investorEncompassTemplateService.getTemplatesByInvestorId(investorId);
      const linked = allTemplates.filter((t) => selectedTemplateIds.includes(t.encompassTemplateId));

      if (linked.length > 0) {
        toast.success(linked.length === 1 ? 'Template linked successfully' : `${linked.length} templates linked successfully`);
        onLinkSuccess(linked[0]);
        onOpenChange(false);
      } else {
        throw new Error('Templates were linked but could not be retrieved');
      }
    } catch (err: unknown) {
      log.error('Failed to link template', { investorId, selectedTemplateIds }, err instanceof Error ? err : undefined);
      const errorMessage = err instanceof Error ? err.message : 'Failed to link template';

      // Check if template is already linked to another investor
      const isAlreadyLinked = errorMessage.includes('already linked to');
      if (isAlreadyLinked) {
        setError(errorMessage);
        toast.error('Template is linked to another investor');
      } else {
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } finally {
      setIsLinking(false);
    }
  };

  const allScopedTemplateIds = [
    ...existingInvestorTemplates.map((template) => template.encompassTemplateId),
    ...selectedTemplateIds
  ];
  const selectedFirstWord = [
    ...existingInvestorTemplates.map((template) => template.templateName),
    ...selectedTemplateIds.map((id) => availableTemplates.find((template) => template.id === id)?.templateName)
  ]
    .filter((name): name is string => Boolean(name))
    .map(firstWord)[0];
  const canSubmit = selectedTemplateIds.length > 0
    && (perLoan || allScopedTemplateIds.every((id) => (productScopes[id] ?? []).length > 0))
    && !isLinking
    && !isLoading;

  const toggleTemplate = (templateId: string, checked: boolean) => {
    setSelectedTemplateIds((prev) => checked ? [...prev, templateId] : prev.filter((id) => id !== templateId));
    if (!checked) {
      setProductScopes((prev) => {
        const next = { ...prev };
        delete next[templateId];
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Link Encompass Template
          </DialogTitle>
          <DialogDescription>
            Select an Encompass Investor Template to link to this investor.
            Delivery contacts will be synced automatically.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Error Alert */}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading templates from Encompass...
              </span>
            </div>
          )}

          {/* Template Selection */}
          {!isLoading && (
            <>
              {!perLoan && existingInvestorTemplates.length > 0 && (
                <div className="space-y-2 rounded-md border border-border bg-muted/20 p-3">
                  <div>
                    <h4 className="text-sm font-medium">Current linked template scopes</h4>
                    <p className="text-xs text-muted-foreground">
                      Adjust existing products here before assigning them to a new template.
                    </p>
                  </div>
                  {existingInvestorTemplates.map((template) => (
                    <div key={template.id} className="space-y-1 rounded-md bg-background p-2">
                      <div className="text-sm font-medium">{template.templateName}</div>
                      <ProductScopeGrid
                        selected={productScopes[template.encompassTemplateId] ?? []}
                        onChange={(products) =>
                          setProductScopes((prev) => ({
                            ...prev,
                            [template.encompassTemplateId]: products
                          }))
                        }
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="template-select">Encompass Template</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchAvailableTemplates}
                    disabled={isLoading}
                    className="h-7"
                  >
                    <RefreshCw className={`h-3.5 w-3.5 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
                <div id="template-select" className="max-h-[360px] space-y-2 overflow-auto rounded-md border border-border p-2">
                  {availableTemplates.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">No templates available</p>
                  ) : (
                    availableTemplates.map((template) => {
                      const linkInfo = linkedTemplates.get(template.id);
                      const isLinked = !!linkInfo;
                      const isLinkedToThisInvestor = linkInfo && linkInfo.investorId === investorId;
                      const selected = selectedTemplateIds.includes(template.id);
                      const incompatibleFirstWord = Boolean(selectedFirstWord) && !selected && firstWord(template.templateName) !== selectedFirstWord;

                      return (
                        <div key={template.id} className={`rounded-md border p-3 ${selected ? 'border-primary bg-primary/5' : 'border-border'}`}>
                          <label className="flex items-start gap-3">
                            <Checkbox
                              checked={selected}
                              disabled={isLinked || isLinking || incompatibleFirstWord}
                              onCheckedChange={(value) => toggleTemplate(template.id, value === true)}
                            />
                            <span className="min-w-0 flex-1">
                              <span className={`block text-sm font-medium ${isLinked ? 'text-muted-foreground' : 'text-foreground'}`}>
                                {template.templateName}
                              </span>
                              {isLinkedToThisInvestor ? (
                                <span className="text-xs text-green-600">Already linked to this investor. Adjust scope above.</span>
                              ) : isLinked ? (
                                <span className="text-xs text-amber-600">Linked to {linkInfo?.investorName}</span>
                              ) : incompatibleFirstWord ? (
                                <span className="text-xs text-muted-foreground">First word does not match selected template group.</span>
                              ) : template.investorName ? (
                                <span className="text-xs text-muted-foreground">
                                  {template.investorName}
                                  {template.investorCode && ` (${template.investorCode})`}
                                </span>
                              ) : null}
                            </span>
                          </label>
                          {selected && !perLoan && (
                            <div className="mt-3 pl-8">
                              <Label className="mb-1 block text-xs text-muted-foreground">Applicable products</Label>
                              <ProductScopeGrid
                                selected={productScopes[template.id] ?? []}
                                onChange={(products) => setProductScopes((prev) => ({ ...prev, [template.id]: products }))}
                              />
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLinking}
          >
            Cancel
          </Button>
          <Button onClick={handleLink} disabled={!canSubmit}>
            {isLinking ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Linking...
              </>
            ) : (
              <>
                <Link2 className="mr-2 h-4 w-4" />
                Link Template{selectedTemplateIds.length > 1 ? 's' : ''}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
